// Shared pagination helpers for listing pages.
//
// Every listing (Blog, Resources, Newsroom, Partners, …) grew its own "Load
// More" out of component state, so a paginated series had no addressable pages
// and search engines could not reach past the first one. These helpers are the
// single place that maps between the URL and that state, so each listing only
// has to say "give me the link to the next page".
//
// Two conventions meet here and they are deliberately not the same:
//
//   * The URL is 1-based — `?page=2` is the second page. That is what a visitor
//     reads and what a search engine expects to index.
//   * Component state and both data sources are 0-based — Algolia's `page` and
//     the Drupal endpoint's `from = page * count` both count from zero.
//
// All conversion happens in this file. No caller should ever have to remember
// which convention the number in its hand is using.

export const PAGE_PARAM = 'page';

// The search term a listing was filtered by. Lives in the URL so a reload or a
// shared link restores the same results, and so pagination can be layered on
// top of it (`?search=cloud&page=2`).
export const SEARCH_PARAM = 'search';

// Route parameters that Next merges into `router.query` alongside the real
// query string. They are part of the path, not the search, and must never be
// written back out as `?slug=…`.
const ROUTE_PARAMS = ['slug', 'lng'];

/**
 * Removes the given keys and the route parameters from a query object.
 *
 * The route parameters matter as much as the named ones. Next merges the
 * catch-all's `slug` into `router.query`, and the listings decide "did the
 * visitor arrive with filters applied?" by asking whether anything is left in
 * that object. Two of them only skipped `url`, so `slug` — present on every
 * single page — counted as a filter. That ran the filter-applied path on every
 * load, which rewrites the URL back to page one and stripped a hand-typed
 * `?page=2` before anyone saw it.
 */
const dropNonFilterKeys = (query, keys) => {
  if (!query) {
    return query;
  }

  const drop = new Set([...keys, ...ROUTE_PARAMS]);

  return Object.fromEntries(
    Object.entries(query).filter(([key]) => !drop.has(key))
  );
};

/**
 * Reads the page number out of a query object and returns it 0-based.
 * Anything unparseable, negative, or absent means page one.
 */
/**
 * Whether the URL carries a page value that is not a page: `?page=abc`,
 * `?page=0`, `?page=-1`. getPageFromQuery quietly reads those as page one;
 * listings that care can redirect them to the bare URL instead of serving a
 * duplicate of page one under another address.
 *
 * No `page` at all is not invalid — that is page one.
 */
export const hasInvalidPageParam = (query) => {
  const raw = query?.[PAGE_PARAM];

  if (raw === undefined) {
    return false;
  }

  const value = Array.isArray(raw) ? raw[0] : raw;

  return !/^[1-9]\d*$/.test(String(value));
};

export const getPageFromQuery = (query) => {
  const raw = query?.[PAGE_PARAM];
  const value = Array.isArray(raw) ? raw[0] : raw;
  const parsed = parseInt(value, 10);

  if (!Number.isFinite(parsed) || parsed < 1) {
    return 0;
  }

  return parsed - 1;
};

/**
 * Builds the href for a given 0-based page, preserving everything else already
 * in the URL.
 *
 * Takes `router.asPath` rather than `router.query` on purpose: `asPath` is the
 * URL as the visitor sees it, so the catch-all route's `slug` segments stay in
 * the path where they belong instead of leaking into the search string.
 *
 * Page one drops the parameter entirely, so the first page of a series has one
 * address rather than two (`/resources/blog` and `/resources/blog?page=1`
 * would otherwise be duplicates of each other).
 */
export const buildPageHref = (asPath, page) => {
  const [path, search = ''] = String(asPath || '/').split('?');
  const params = new URLSearchParams(search);

  ROUTE_PARAMS.forEach((param) => params.delete(param));

  if (!page || page < 1) {
    params.delete(PAGE_PARAM);
  } else {
    params.set(PAGE_PARAM, String(page + 1));
  }

  const queryString = params.toString();

  return queryString ? `${path}?${queryString}` : path;
};

/**
 * The query without the pagination parameter.
 *
 * Several listings hydrate their filters by treating every entry in
 * `router.query` as a filter — "is there anything in the query string? then the
 * visitor arrived with filters applied". `page` is in that same query string
 * but is not a filter, and letting it through makes those listings believe a
 * filter was applied, which resets the page back to one. Reloading `?page=2`
 * then silently showed page one.
 *
 * Any component that reads the whole query as filter state must pass it through
 * here first.
 */
export const stripPageParam = (query) => dropNonFilterKeys(query, [PAGE_PARAM]);

/**
 * The query with neither the page nor the search parameter — i.e. only what a
 * listing should treat as filter state. `search` drives the search box, not the
 * filters, so it must be excluded for the same reason `page` is.
 */
export const stripListingParams = (query) =>
  dropNonFilterKeys(query, [PAGE_PARAM, SEARCH_PARAM]);

/** The search term from a query object, or '' when there is none. */
export const getSearchFromQuery = (query) => {
  const raw = query?.[SEARCH_PARAM];
  const value = Array.isArray(raw) ? raw[0] : raw;

  return typeof value === 'string' ? value : '';
};

/**
 * The href for a given search term, preserving everything else in the URL.
 *
 * Always drops the page: a new search starts at the first page, and carrying
 * the old page number over would land the visitor somewhere in the middle of
 * results they have not seen — or past the end of them.
 */
export const buildSearchHref = (asPath, term) => {
  const [path, search = ''] = String(asPath || '/').split('?');
  const params = new URLSearchParams(search);

  ROUTE_PARAMS.forEach((param) => params.delete(param));
  params.delete(PAGE_PARAM);

  if (term) {
    params.set(SEARCH_PARAM, term);
  } else {
    params.delete(SEARCH_PARAM);
  }

  const queryString = params.toString();

  return queryString ? `${path}?${queryString}` : path;
};

/**
 * Whether an Algolia response says more pages exist.
 *
 * Written as an explicit type check because the raw comparison is unsafe. The
 * listings initialise their state to `[]`, and on `[]` the obvious condition
 * `data.page + 1 !== data.nbPages && data.nbHits !== 0` evaluates to
 * `NaN !== undefined && undefined !== 0` — true on both counts. So whenever a
 * server fetch timed out or failed, the listing rendered a next-page link with
 * no data behind it, pointing a crawler at a page that does not exist.
 */
export const hasMoreAlgoliaPages = (algoliaData) => {
  const nbPages = algoliaData?.nbPages;
  const nbHits = algoliaData?.nbHits;

  if (typeof nbPages !== 'number' || typeof nbHits !== 'number') {
    return false;
  }

  return nbHits > 0 && (algoliaData.page ?? 0) + 1 < nbPages;
};

/**
 * Total number of pages in a series. Returns 0 for an empty result set so
 * callers can distinguish "no results" from "one page of results".
 */
export const getTotalPages = (totalItems, perPage) => {
  if (!totalItems || !perPage || perPage < 1) {
    return 0;
  }

  return Math.ceil(totalItems / perPage);
};

/**
 * Whether a next page exists. `totalPages` is optional: listings backed by an
 * endpoint that does not report a total (the Drupal articles endpoint returns
 * a plain array) pass the size of the last response instead, and a short page
 * means the end of the series.
 */
export const hasNextPage = ({ page, totalPages, lastPageSize, perPage }) => {
  if (typeof totalPages === 'number' && totalPages > 0) {
    return page + 1 < totalPages;
  }

  if (typeof lastPageSize === 'number' && typeof perPage === 'number') {
    return lastPageSize >= perPage;
  }

  return false;
};
